import { ReactElement, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	EuiFieldText,
	EuiFormRow,
	EuiPage,
	EuiPageBody,
	EuiPageHeader,
	EuiSpacer,
} from '@equipmentshare/ds2'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import { heapEvent } from '@equipmentshare/heaper'
import {
	DimensionUom,
	ItemMasterVariantUom,
	NuminaSerialType,
	WeightUnit,
	useCreateItemMasterMutation,
	useCreateProductWithItemMasterMutation,
} from '../../../../graphql'
import { Part, Provider } from 'graphql'
import ProviderPicker from 'components/ProviderPicker'
import PartPicker from 'components/PartPicker'
import EquipmentMakeModelPicker from '../components/EquipmentMakeModelPicker'
import FormFooter, { FORM_FOOTER_HEIGHT, ValidationSummary } from '../components/FormFooter'
import {
	isValidSiocQuantity,
	siocQuantityForPayload,
	siocToggleChanges,
	dimensionsForPayload,
	weightForPayload,
	SIOC_QUANTITY_ERROR,
	isComplete,
	getCompletenessErrors,
	toErrorMap,
	ASSET_MAKE_MODEL_ERROR,
	BACKEND_SUPPORTS_DRAFT_CREATE,
	DRAFT_CREATE_UNSUPPORTED_MESSAGE,
} from '../utils'

type ItemMasterFields = {
	flammable: boolean,
	fragile: boolean,
	lithiumIonBattery: boolean,
	isHazmat: boolean,
	numinaSerialType: NuminaSerialType,
	equipmentMakeId: number | null,
	equipmentModelId: number | null,
	dimensionLength: string,
	dimensionWidth: string,
	dimensionHeight: string,
	dimensionUnit: DimensionUom,
	weightAmount: string,
	weightUnit: WeightUnit,
	freightClass: string,
	nmfc: string,
	shippedInOriginalContainer: boolean,
	siocQuantity: string,
	isPolybagEligible: boolean,
	uom: ItemMasterVariantUom,
}

const defaultItemMasterFields: ItemMasterFields = {
	flammable: false,
	fragile: false,
	lithiumIonBattery: false,
	isHazmat: false,
	numinaSerialType: NuminaSerialType.Bulk,
	equipmentMakeId: null,
	equipmentModelId: null,
	dimensionLength: '',
	dimensionWidth: '',
	dimensionHeight: '',
	dimensionUnit: DimensionUom.In,
	weightAmount: '',
	weightUnit: WeightUnit.Lb,
	freightClass: '',
	nmfc: '',
	shippedInOriginalContainer: false,
	siocQuantity: '',
	isPolybagEligible: false,
	uom: ItemMasterVariantUom.Each,
}

function ItemMasterFieldsForm( {
	fields,
	onChange,
	fieldErrors,
}: {
	fields: ItemMasterFields,
	onChange: ( updated: Partial<ItemMasterFields> ) => void,
	fieldErrors: Record<string, string>,
} ): ReactElement {
	return (
		<Box>
			<Typography variant="subtitle2" mb={2}>Item Master / Base Variant</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={1}>
				Weight and dimensions can be left blank to save a draft. They are required to complete the item master and to add it to a distribution center.
			</Typography>
			<Stack spacing={2}>
				<Stack direction="row" spacing={2}>
					<TextField
						label="Length"
						value={fields.dimensionLength}
						onChange={( e ) => onChange( { dimensionLength: e.target.value } )}
						size="small"
						type="number"
						error={Boolean( fieldErrors.dimensionLength )}
						helperText={fieldErrors.dimensionLength}
					/>
					<TextField
						label="Width"
						value={fields.dimensionWidth}
						onChange={( e ) => onChange( { dimensionWidth: e.target.value } )}
						size="small"
						type="number"
						error={Boolean( fieldErrors.dimensionWidth )}
						helperText={fieldErrors.dimensionWidth}
					/>
					<TextField
						label="Height"
						value={fields.dimensionHeight}
						onChange={( e ) => onChange( { dimensionHeight: e.target.value } )}
						size="small"
						type="number"
						error={Boolean( fieldErrors.dimensionHeight )}
						helperText={fieldErrors.dimensionHeight}
					/>
					<Select
						value={fields.dimensionUnit}
						onChange={( e ) => onChange( { dimensionUnit: e.target.value as DimensionUom } )}
						size="small"
						label="Unit"
					>
						{Object.values( DimensionUom ).map( ( u ) => (
							<MenuItem key={u} value={u}>{u}</MenuItem>
						) )}
					</Select>
				</Stack>
				<Stack direction="row" spacing={2} alignItems="flex-start">
					<TextField
						label="Weight"
						value={fields.weightAmount}
						onChange={( e ) => onChange( { weightAmount: e.target.value } )}
						size="small"
						type="number"
						error={Boolean( fieldErrors.weightAmount )}
						helperText={fieldErrors.weightAmount}
					/>
					<Select
						value={fields.weightUnit}
						onChange={( e ) => onChange( { weightUnit: e.target.value as WeightUnit } )}
						size="small"
					>
						{Object.values( WeightUnit ).map( ( u ) => (
							<MenuItem key={u} value={u}>{u}</MenuItem>
						) )}
					</Select>
				</Stack>
				<TextField
					label="Freight Class"
					value={fields.freightClass}
					onChange={( e ) => onChange( { freightClass: e.target.value } )}
					size="small"
				/>
				<TextField
					label="NMFC"
					value={fields.nmfc}
					onChange={( e ) => onChange( { nmfc: e.target.value } )}
					size="small"
				/>
				<Box>
					<Typography variant="caption" color="text.secondary">Unit of Measure</Typography>
					<Select
						value={fields.uom}
						onChange={( e ) => onChange( { uom: e.target.value as ItemMasterVariantUom } )}
						size="small"
						fullWidth
					>
						{Object.values( ItemMasterVariantUom ).map( ( u ) => (
							<MenuItem key={u} value={u}>{u}</MenuItem>
						) )}
					</Select>
				</Box>
				<Box>
					<Typography variant="caption" color="text.secondary">Numina Serial Type</Typography>
					<Select
						value={fields.numinaSerialType}
						onChange={( e ) => {
							const next = e.target.value as NuminaSerialType
							if ( next === NuminaSerialType.Asset ) {
								onChange( { numinaSerialType: next } )
							} else {
								// Switching away from Asset invalidates the Make/Model pair.
								onChange( { numinaSerialType: next, equipmentMakeId: null, equipmentModelId: null } )
							}
						}}
						size="small"
						fullWidth
					>
						{Object.values( NuminaSerialType ).map( ( t ) => (
							<MenuItem key={t} value={t}>{t}</MenuItem>
						) )}
					</Select>
				</Box>
				{fields.numinaSerialType === NuminaSerialType.Asset && (
					<EquipmentMakeModelPicker
						makeId={fields.equipmentMakeId}
						modelId={fields.equipmentModelId}
						onChange={( next ) => onChange( { equipmentMakeId: next.makeId, equipmentModelId: next.modelId } )}
						required
					/>
				)}
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.isHazmat}
							onChange={( e ) => onChange( { isHazmat: e.target.checked } )}
						/>
					}
					label="Hazmat"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.flammable}
							onChange={( e ) => onChange( { flammable: e.target.checked } )}
						/>
					}
					label="Flammable"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.fragile}
							onChange={( e ) => onChange( { fragile: e.target.checked } )}
						/>
					}
					label="Fragile"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.lithiumIonBattery}
							onChange={( e ) => onChange( { lithiumIonBattery: e.target.checked } )}
						/>
					}
					label="Lithium Ion Battery"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.shippedInOriginalContainer}
							onChange={( e ) => onChange( siocToggleChanges( e.target.checked ) )}
						/>
					}
					label="Shipped in Original Container"
				/>
				<TextField
					label="SIOC Quantity"
					value={fields.shippedInOriginalContainer ? fields.siocQuantity : ''}
					onChange={( e ) => onChange( { siocQuantity: e.target.value } )}
					size="small"
					type="number"
					disabled={!fields.shippedInOriginalContainer}
					required={fields.shippedInOriginalContainer}
					error={!isValidSiocQuantity( fields )}
					helperText={ !isValidSiocQuantity( fields ) ? SIOC_QUANTITY_ERROR : undefined }
					inputProps={{ min: 1 }}
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.isPolybagEligible}
							onChange={( e ) => onChange( { isPolybagEligible: e.target.checked } )}
							disabled={fields.shippedInOriginalContainer}
						/>
					}
					label="Polybag Eligible"
				/>
			</Stack>
		</Box>
	)
}

function NewProductMode(): ReactElement {
	const navigate = useNavigate()
	const [ manufacturer, setManufacturer ] = useState<Provider | undefined>( undefined )
	const [ manufacturerNumber, setManufacturerNumber ] = useState( '' )
	const [ productName, setProductName ] = useState( '' )
	const [ msrp, setMsrp ] = useState( '' )
	const [ fields, setFields ] = useState<ItemMasterFields>( defaultItemMasterFields )

	const [ submitted, setSubmitted ] = useState( false )
	const [ apiError, setApiError ] = useState<string | null>( null )

	const [ createProduct, { loading } ] = useCreateProductWithItemMasterMutation()

	const handleFieldChange = ( updated: Partial<ItemMasterFields> ) => {
		setFields( ( prev ) => ( { ...prev, ...updated } ) )
	}

	const isAsset = fields.numinaSerialType === NuminaSerialType.Asset
	const assetIdsMissing = isAsset && ( !fields.equipmentMakeId || !fields.equipmentModelId )

	// Draft tier: identity + SIOC + asset make/model. These block any save.
	const draftErrors: { field: string, message: string }[] = []
	if ( !manufacturer ) {
		draftErrors.push( { field: 'manufacturer', message: 'Manufacturer is required' } )
	}
	if ( !manufacturerNumber ) {
		draftErrors.push( { field: 'manufacturerNumber', message: 'Manufacturer number is required' } )
	}
	if ( !productName ) {
		draftErrors.push( { field: 'productName', message: 'Product name is required' } )
	}
	if ( assetIdsMissing ) {
		draftErrors.push( { field: 'equipmentMakeModel', message: ASSET_MAKE_MODEL_ERROR } )
	}
	if ( !isValidSiocQuantity( fields ) ) {
		draftErrors.push( { field: 'siocQuantity', message: SIOC_QUANTITY_ERROR } )
	}
	const draftValid = draftErrors.length === 0

	const completenessErrors = getCompletenessErrors( fields )
	const recordComplete = isComplete( fields )

	// Adaptive intent: a fully-filled record saves as Complete; otherwise Draft.
	const savingDraft = !recordComplete
	// Until the backend accepts optional dims/weight, the draft path is blocked.
	const draftBlockedByBackend = savingDraft && !BACKEND_SUPPORTS_DRAFT_CREATE

	const fieldErrors = submitted ? toErrorMap( draftBlockedByBackend ? completenessErrors : [] ) : {}

	const summary: ValidationSummary | null = ( () => {
		if ( !submitted ) {
			return null
		}
		if ( !draftValid ) {
			return { title: 'Fix the following to save:', errors: draftErrors }
		}
		if ( apiError ) {
			return { title: 'Could not save item master', errors: [ { field: 'api', message: apiError } ] }
		}
		if ( draftBlockedByBackend ) {
			return {
				title: 'Weight and dimensions are required',
				errors: [
					{ field: 'draft', message: DRAFT_CREATE_UNSUPPORTED_MESSAGE },
					...completenessErrors,
				],
			}
		}
		return null
	} )()

	const submitLabel = savingDraft ? 'Save Draft' : 'Save'

	const handleSubmit = async() => {
		setSubmitted( true )
		setApiError( null )
		if ( !manufacturer || !draftValid ) {
			return
		}
		// Guard: blank dims/weight cannot be persisted on create until the API supports it.
		if ( draftBlockedByBackend ) {
			return
		}

		try {
			const result = await createProduct( {
				variables: {
					manufacturerId: manufacturer.id,
					manufacturerNumber,
					name: productName,
					msrp: msrp ? Number( msrp ) : undefined,
					itemMaster: {
						flammable: fields.flammable,
						fragile: fields.fragile,
						lithiumIonBattery: fields.lithiumIonBattery,
						isHazmat: fields.isHazmat,
						numinaSerialType: fields.numinaSerialType,
						equipmentMakeId: isAsset ? String( fields.equipmentMakeId ) : null,
						equipmentModelId: isAsset ? String( fields.equipmentModelId ) : null,
						shippedInOriginalContainer: fields.shippedInOriginalContainer,
						siocQuantity: siocQuantityForPayload( fields ),
						isPolybagEligible: fields.isPolybagEligible,
						uom: fields.uom,
						// Omitted (not sent as 0/unit-only) when blank so a draft is not
						// rejected by the backend's all-or-nothing dims+weight validation.
						dimensions: dimensionsForPayload( fields ),
						weight: weightForPayload( fields ),
						freightClass: fields.freightClass || undefined,
						nmfc: fields.nmfc || undefined,
					},
				},
			} )

			if ( result.data?.createProduct?.success ) {
				const newItemMasterId = result.data?.createProduct?.product?.itemMaster?.id ?? ''
				heapEvent( 'Inventory - Item Master - Create - Submit - Success', { item_master_id: newItemMasterId, draft: savingDraft } )
				navigate( newItemMasterId ? `/manage/item-masters/${newItemMasterId}` : '/manage/item-masters' )
			} else {
				setApiError( result.data?.createProduct?.message ?? 'Something went wrong. Please try again.' )
				heapEvent( 'Inventory - Item Master - Create - Submit - Error', {} )
			}
		} catch {
			setApiError( 'Something went wrong. Please try again.' )
			heapEvent( 'Inventory - Item Master - Create - Submit - Error', {} )
		}
	}

	return (
		<Stack spacing={3} sx={{ pb: `${FORM_FOOTER_HEIGHT}px` }}>
			<Box>
				<Typography variant="subtitle2" mb={2}>Product Details</Typography>
				<Stack spacing={2}>
					<ProviderPicker
						label="Manufacturer"
						onChange={( _id, provider ) => setManufacturer( provider )}
						selectedProvider={manufacturer}
						required
					/>
					<EuiFormRow label="Manufacturer Number">
						<EuiFieldText
							value={manufacturerNumber}
							onChange={( e ) => setManufacturerNumber( e.target.value )}
						/>
					</EuiFormRow>
					<EuiFormRow label="Product Name">
						<EuiFieldText
							value={productName}
							onChange={( e ) => setProductName( e.target.value )}
						/>
					</EuiFormRow>
					<EuiFormRow label="MSRP">
						<EuiFieldText
							value={msrp}
							onChange={( e ) => setMsrp( e.target.value )}
							type="number"
						/>
					</EuiFormRow>
				</Stack>
			</Box>

			<ItemMasterFieldsForm fields={fields} onChange={handleFieldChange} fieldErrors={fieldErrors} />

			<FormFooter summary={summary}>
				<Button variant="outlined" onClick={() => navigate( '/manage/item-masters' )} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={loading || !draftValid || draftBlockedByBackend}
				>
					{loading ? 'Creating...' : submitLabel}
				</Button>
			</FormFooter>
		</Stack>
	)
}

function ExistingProductMode(): ReactElement {
	const navigate = useNavigate()
	const [ selectedProduct, setSelectedProduct ] = useState<Part | undefined>( undefined )
	const [ fields, setFields ] = useState<ItemMasterFields>( defaultItemMasterFields )

	const [ submitted, setSubmitted ] = useState( false )
	const [ apiError, setApiError ] = useState<string | null>( null )

	const [ createItemMaster, { loading } ] = useCreateItemMasterMutation()

	const handleFieldChange = ( updated: Partial<ItemMasterFields> ) => {
		setFields( ( prev ) => ( { ...prev, ...updated } ) )
	}

	const isAsset = fields.numinaSerialType === NuminaSerialType.Asset
	const assetIdsMissing = isAsset && ( !fields.equipmentMakeId || !fields.equipmentModelId )

	// Draft tier: selected product + SIOC + asset make/model. These block any save.
	const draftErrors: { field: string, message: string }[] = []
	if ( !selectedProduct ) {
		draftErrors.push( { field: 'product', message: 'Product is required' } )
	}
	if ( assetIdsMissing ) {
		draftErrors.push( { field: 'equipmentMakeModel', message: ASSET_MAKE_MODEL_ERROR } )
	}
	if ( !isValidSiocQuantity( fields ) ) {
		draftErrors.push( { field: 'siocQuantity', message: SIOC_QUANTITY_ERROR } )
	}
	const draftValid = draftErrors.length === 0

	const completenessErrors = getCompletenessErrors( fields )
	const recordComplete = isComplete( fields )

	const savingDraft = !recordComplete
	const draftBlockedByBackend = savingDraft && !BACKEND_SUPPORTS_DRAFT_CREATE

	const fieldErrors = submitted ? toErrorMap( draftBlockedByBackend ? completenessErrors : [] ) : {}

	const summary: ValidationSummary | null = ( () => {
		if ( !submitted ) {
			return null
		}
		if ( !draftValid ) {
			return { title: 'Fix the following to save:', errors: draftErrors }
		}
		if ( apiError ) {
			return { title: 'Could not save item master', errors: [ { field: 'api', message: apiError } ] }
		}
		if ( draftBlockedByBackend ) {
			return {
				title: 'Weight and dimensions are required',
				errors: [
					{ field: 'draft', message: DRAFT_CREATE_UNSUPPORTED_MESSAGE },
					...completenessErrors,
				],
			}
		}
		return null
	} )()

	const submitLabel = savingDraft ? 'Save Draft' : 'Save'

	const handleSubmit = async() => {
		setSubmitted( true )
		setApiError( null )
		if ( !selectedProduct || !draftValid ) {
			return
		}
		if ( draftBlockedByBackend ) {
			return
		}

		try {
			const result = await createItemMaster( {
				variables: {
					input: {
						flammable: fields.flammable,
						fragile: fields.fragile,
						lithiumIonBattery: fields.lithiumIonBattery,
						isHazmat: fields.isHazmat,
						numinaSerialType: fields.numinaSerialType,
						equipmentMakeId: isAsset ? String( fields.equipmentMakeId ) : null,
						equipmentModelId: isAsset ? String( fields.equipmentModelId ) : null,
						variants: [
							{
								isBase: true,
								productId: selectedProduct.id.toString(),
								uom: fields.uom,
								shippedInOriginalContainer: fields.shippedInOriginalContainer,
								siocQuantity: siocQuantityForPayload( fields ),
								isPolybagEligible: fields.isPolybagEligible,
								// Omitted when blank so a draft is not rejected by the backend's
								// all-or-nothing dims+weight validation.
								dimensions: dimensionsForPayload( fields ),
								weight: weightForPayload( fields ),
								freightClass: fields.freightClass || undefined,
								nmfc: fields.nmfc || undefined,
							},
						],
					},
				},
			} )

			const newId = result.data?.createItemMaster?.itemMaster?.id
			if ( result.data?.createItemMaster?.success && newId ) {
				heapEvent( 'Inventory - Item Master - Create - Submit - Success', { item_master_id: newId, draft: savingDraft } )
				navigate( `/manage/item-masters/${newId}` )
			} else {
				setApiError( result.data?.createItemMaster?.message ?? 'Something went wrong. Please try again.' )
				heapEvent( 'Inventory - Item Master - Create - Submit - Error', {} )
			}
		} catch {
			setApiError( 'Something went wrong. Please try again.' )
			heapEvent( 'Inventory - Item Master - Create - Submit - Error', {} )
		}
	}

	return (
		<Stack spacing={3} sx={{ pb: `${FORM_FOOTER_HEIGHT}px` }}>
			<Box>
				<Typography variant="subtitle2" mb={2}>Select Product</Typography>
				<PartPicker
					label="Product"
					selectedPart={selectedProduct}
					onChange={( _id, part ) => setSelectedProduct( part )}
					required
				/>
			</Box>

			<ItemMasterFieldsForm fields={fields} onChange={handleFieldChange} fieldErrors={fieldErrors} />

			<FormFooter summary={summary}>
				<Button variant="outlined" onClick={() => navigate( '/manage/item-masters' )} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={loading || !draftValid || draftBlockedByBackend}
				>
					{loading ? 'Creating...' : submitLabel}
				</Button>
			</FormFooter>
		</Stack>
	)
}

export default function ItemMasterCreate(): ReactElement {
	const [ mode, setMode ] = useState( 0 )

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody>
				<EuiPageHeader pageTitle="Create Item Master" paddingSize="none" />
				<EuiSpacer />

				<Tabs value={mode} onChange={( _, v ) => setMode( v )}>
					<Tab label="New Product" />
					<Tab label="Existing Product" />
				</Tabs>

				<Box mt={3}>
					{mode === 0 ? <NewProductMode /> : <ExistingProductMode />}
				</Box>
			</EuiPageBody>
		</EuiPage>
	)
}
